import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { resolveOSDuration } from '@/services/ordens-servico.service';
import { formatarTempo, getStatusOSLabels } from '@/types/ordemServico';
import type { AgendaOS } from '@/services/agenda.service';

interface MinhaOSDetalheDialogProps {
  os: AgendaOS | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MinhaOSDetalheDialog({ os, open, onOpenChange }: MinhaOSDetalheDialogProps) {
  const { t, i18n } = useTranslation();
  if (!os) return null;

  const statusLabels = getStatusOSLabels(t);

  const dataHora = os.data_agendada
    ? new Date(os.data_agendada).toLocaleString(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

  const servicos = Array.from(
    new Set(os.itens.map((i) => i.servico_nome).filter((n): n is string => !!n)),
  );

  const duracao = resolveOSDuration({
    duracao_estimada_minutos: os.duracao_estimada_minutos,
    itens: os.itens,
  });

  const statusLabel =
    statusLabels[os.status as keyof typeof statusLabels] ?? os.status;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{os.numero_os}</DialogTitle>
        </DialogHeader>
        <dl className="space-y-3 text-sm">
          <Row label={t('myCalendar.detail.status')} value={statusLabel} />
          <Row label={t('myCalendar.detail.dateTime')} value={dataHora} />
          <Row label={t('myCalendar.detail.client')} value={os.cliente?.nome || '—'} />
          <Row
            label={t('myCalendar.detail.services')}
            value={servicos.length > 0 ? servicos.join(', ') : '—'}
          />
          <Row label={t('myCalendar.detail.duration')} value={formatarTempo(duracao)} />
        </dl>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}
